import {
  IconBell,
  IconBellFilled,
  IconCirclePlus,
  IconCirclePlusFilled,
  IconHome,
  IconHomeFilled,
  IconLayoutGrid,
  IconLayoutGridFilled,
  IconMessages,
  IconMessagesFilled,
  IconUserCircle,
} from '@tabler/icons-react-native';
import type { StyleProp, ViewStyle } from 'react-native';
import { Box, Text, useTheme, type Theme } from 'app/shopify/theme';
import { Avatar } from '../../media/Avatar/Avatar';
import { NavigationPressable, type NavigationState } from '../Navigation/NavigationPressable';

export type TabBarIcon = 'home' | 'sharings' | 'publish' | 'messages' | 'notifications' | 'profile';

export interface NavigationItem {
  key: string;
  label: string;
  icon: TabBarIcon;
  disabled?: boolean;
  badge?: boolean;
  /** Include unread information here when badge is shown. */
  accessibilityLabel?: string;
  avatar?: React.ReactNode;
}

export interface TabBarItemProps extends Omit<NavigationItem, 'key'> {
  active?: boolean;
  placement?: 'bottom' | 'header';
  state?: NavigationState;
  onPress?: () => void;
  style?: StyleProp<ViewStyle>;
}

const ICONS = {
  home: [IconHome, IconHomeFilled],
  sharings: [IconLayoutGrid, IconLayoutGridFilled],
  publish: [IconCirclePlus, IconCirclePlusFilled],
  messages: [IconMessages, IconMessagesFilled],
  notifications: [IconBell, IconBellFilled],
  profile: [IconUserCircle, IconUserCircle],
} as const;

function getPresentation(
  theme: Theme,
  icon: TabBarIcon,
  active: boolean,
  placement: 'bottom' | 'header',
  avatar: React.ReactNode,
) {
  const isHeader = placement === 'header';
  const showAvatar = icon === 'profile' && (active || (isHeader && avatar !== undefined && avatar !== null));
  const onHeader = isHeader || icon === 'publish';
  return {
    showAvatar,
    Icon: ICONS[icon][active ? 1 : 0],
    iconColor: theme.colors[onHeader ? 'white-label.header.on-header' : 'icon.primary'],
    textColor: onHeader ? ('white-label.header.on-header' as const) : ('text.primary' as const),
    paddingHorizontal: isHeader || showAvatar ? theme.spacing['spacing.general.x2'] : 0,
    backgroundColor: isHeader && active && !showAvatar ? theme.colors['state-layer.press'] : undefined,
  };
}

export function TabBarItem({
  label,
  icon,
  active = false,
  placement = 'bottom',
  state,
  disabled,
  badge = false,
  accessibilityLabel = label,
  avatar,
  onPress,
  style,
}: TabBarItemProps) {
  const theme = useTheme();
  const { showAvatar, Icon, iconColor, textColor, paddingHorizontal, backgroundColor } = getPresentation(
    theme,
    icon,
    active,
    placement,
    avatar,
  );
  const iconSize = Number(theme.tokens.sizing['sizing.icon.default']);
  return (
    <NavigationPressable
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected: active }}
      disabled={disabled}
      state={state}
      onPress={onPress}
      style={[
        {
          minHeight: Number(theme.tokens.sizing['sizing.general.x12']),
          minWidth: Number(theme.tokens.sizing['sizing.general.x12']),
          alignItems: 'center',
          justifyContent: 'center',
          paddingHorizontal,
          gap: theme.spacing['spacing.general.x1'],
          borderRadius: theme.borderRadii['radius.sm'],
          backgroundColor,
        },
        style,
      ]}
    >
      <Box alignItems="center" justifyContent="center">
        {showAvatar ? (
          (avatar ?? <Avatar size={40} media="text" initials={label.slice(0, 1)} />)
        ) : (
          <Icon size={iconSize} color={iconColor} strokeWidth={2} />
        )}
        {badge && (
          <Box
            pointerEvents="none"
            position="absolute"
            top={0}
            right={-theme.spacing['spacing.general.x1']}
            width={Number(theme.tokens.sizing['sizing.general.x3'])}
            height={Number(theme.tokens.sizing['sizing.general.x3'])}
            borderRadius="radius.full"
            backgroundColor="surface.error.default"
            borderColor="surface.default"
            borderWidth={theme.borderWidths['border-width.emphasized']}
          />
        )}
      </Box>
      {!showAvatar && (
        <Text
          variant="bodyXSmallMedium"
          color={textColor}
          textAlign="center"
          paddingHorizontal={placement === 'bottom' ? 'spacing.general.x1' : undefined}
          style={{ alignSelf: 'stretch', flexShrink: 1 }}
        >
          {label}
        </Text>
      )}
    </NavigationPressable>
  );
}
